import * as React from 'react';
import { Icon, Link } from '@fluentui/react';
import styles from './SharePointOrganizer.module.scss';
import type { IIssue, IssueCategory } from '../../../models/Issue';
import type { IScanResult } from '../../../models/ScanResult';
import { CATEGORIES, type ICategoryInfo } from '../../../rules/RulesEngine';
import { filePath, folderLabel } from '../../../rules/ruleUtils';
import { formatBytes } from '../../../utils/format';

export interface IIssueListProps {
  scan: IScanResult;
  category: IssueCategory;
  issues: IIssue[];
}

const SEVERITY_LABEL: { [severity: string]: string } = { high: 'High', medium: 'Medium', low: 'Low' };

/** Issues in one category, each with links to the affected files and folders. */
export default function IssueList(props: IIssueListProps): React.ReactElement {
  const { scan, category, issues } = props;
  const info: ICategoryInfo = CATEGORIES.filter(c => c.category === category)[0];

  return (
    <div className={styles.issueList}>
      <h3 className={styles.sectionTitle}>{info.label}</h3>
      <p className={styles.issueListDescription}>{info.description}</p>
      <ul className={styles.issues}>
        {issues.map(issue => (
          <li key={issue.id} className={styles.issue}>
            <div className={styles.issueHeader}>
              <span className={`${styles.severity} ${styles[`severity_${issue.severity}`]}`}>
                {SEVERITY_LABEL[issue.severity]}
              </span>
              <span className={styles.issueMessage}>{issue.message}</span>
            </div>
            <ul className={styles.issueItems}>
              {issue.folderIds.map(id => scan.folders[id]).map(folder => (
                <li key={folder.id}>
                  <Icon iconName="FabricFolder" className={styles.treeIcon} />
                  <Link href={folder.webUrl} target="_blank" rel="noreferrer">{folderLabel(folder)}</Link>
                </li>
              ))}
              {issue.fileIds.map(id => scan.files[id]).map(file => (
                <li key={file.id}>
                  <Icon iconName="Page" className={styles.treeIcon} />
                  <Link href={file.webUrl} target="_blank" rel="noreferrer">{filePath(file)}</Link>
                  <span className={styles.treeMeta}>{formatBytes(file.size)}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </div>
  );
}
